import type { Schemas } from '@npc/api-client'

export type ActivityItem = Schemas['ActivityItem']

/** 同一 event_id 只留一条；新事件按 occurred_at 插入，新的在前。 */
export function upsertActivityItem(items: ActivityItem[], incoming: ActivityItem): ActivityItem[] {
  const without = items.filter((item) => item.event_id !== incoming.event_id)
  const next = [...without, incoming]
  next.sort((a, b) => {
    const ta = Date.parse(a.occurred_at)
    const tb = Date.parse(b.occurred_at)
    if (tb !== ta) return tb - ta
    return b.stream_seq - a.stream_seq
  })
  return next
}

export function upsertActivityItems(items: ActivityItem[], incoming: ActivityItem[]): ActivityItem[] {
  return incoming.reduce(upsertActivityItem, items)
}

/**
 * 断线：进行中立刻变成未知，并撤掉任何运行动画合同。
 * 不断成失败，也不继续标进行中。
 */
export function degradeRunningOnDisconnect(items: ActivityItem[]): ActivityItem[] {
  return items.map((item) => {
    if (item.status !== 'running') return item
    return {
      ...item,
      status: 'unknown',
      status_label: item.status_label,
    }
  })
}

/** 没有新证据的长时间 running 不能继续冒充正在执行。 */
export function degradeStaleRunning(
  items: ActivityItem[],
  now = Date.now(),
  staleAfterMs = 6 * 60 * 60 * 1000,
): ActivityItem[] {
  return items.map((item) => {
    if (item.status !== 'running' || now - Date.parse(item.occurred_at) < staleAfterMs) return item
    return { ...item, status: 'unknown' }
  })
}

/** 一条 correlation_id 是一件事；列表只显示最新结果，详情保留更新轨迹。 */
export function collapseActivityThreads(items: ActivityItem[]): ActivityItem[] {
  const groups = new Map<string, ActivityItem[]>()
  for (const item of items) {
    const key = item.correlation_id || item.event_id
    const group = groups.get(key) ?? []
    group.push(item)
    groups.set(key, group)
  }
  return [...groups.values()]
    .map((group) => {
      group.sort(compareActivity)
      const latest = group[0]!
      if (group.length === 1) return latest
      const history = [...group].reverse().map((entry) => ({
        state: entry.status,
        label: entry.status_label,
        desc: entry.summary,
        keep: entry.requires_action || ['failed', 'rolled_back', 'platform_held', 'unknown'].includes(entry.status),
      }))
      return {
        ...latest,
        safe_variables: {
          ...latest.safe_variables,
          thread_count: String(group.length),
        },
        stages: [...(latest.stages ?? []), ...history],
      }
    })
    .sort(compareActivity)
}

function compareActivity(a: ActivityItem, b: ActivityItem): number {
  const time = Date.parse(b.occurred_at) - Date.parse(a.occurred_at)
  return time === 0 ? b.stream_seq - a.stream_seq : time
}

export function displayStatus(item: ActivityItem, disconnected: boolean): ActivityItem['status'] {
  if (disconnected && item.status === 'running') return 'unknown'
  return item.status
}

/** 生产未读：stream_seq 大于服务端已读游标的条数。不用 localStorage。 */
export function unreadAfterCursor(items: { stream_seq: number }[], lastReadStreamSeq: number): number {
  return items.filter((item) => item.stream_seq > lastReadStreamSeq).length
}

/** 打开一条线程时要 POST 的 stream_seq。已读或没有选中则不发。不用 localStorage。 */
export function streamSeqToMarkRead(item: { stream_seq: number } | undefined, lastReadStreamSeq: number): number | null {
  if (!item || item.stream_seq <= lastReadStreamSeq) return null
  return item.stream_seq
}
